import { ReactElement, useState } from 'react';
import { useFormContext, get, RegisterOptions } from 'react-hook-form';
import clsx from 'clsx';

type InputProps = {
  label?: string;
  isDisabled?: boolean;
  noMarginTop?: boolean;
  idname: string;
  autoComplete?: string;
  validationObject?: RegisterOptions;
  isHidden?: boolean;
  min?: string;
  max?: string;
};

/**
 * A common Input control component.
 * @param props - Input props.
 * @returns - Returns a react element for input text.
 */
export default function PasswordInputBox(props: InputProps): ReactElement {
  const {
    label,
    isDisabled = false,
    noMarginTop,
    idname,
    autoComplete,
    validationObject,
    isHidden,
    min,
    max
  } = props;
  const {
    register,
    formState: { errors }
  } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div
      style={{ display: isHidden ? 'none' : 'block' }}
      className={`relative w-full ${!noMarginTop && 'mt-4'}`}
    >
      {label && (
        <label htmlFor={idname} className="text-gray-500 dark:text-gray-500 text-sm">
          {label}
          {validationObject?.required ? ' *' : ''}
        </label>
      )}
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          step={0}
          max={max}
          min={min}
          disabled={isDisabled}
          autoComplete={autoComplete}
          id={idname}
          // eslint-disable-next-line
          {...register(idname, validationObject)}
          className={clsx(
            'w-full block py-1.5 px-3.5 text-base font-normal dark:text-gray-300 placeholder-gray-400 border rounded bg-gray-50 dark:bg-container-500 focus:dark:border-primary-300 focus-within:dark:ring-transparent',
            { 'dark:text-gray-500': isDisabled },
            { 'mt-1': !!label },
            { 'border-error-300': get(errors, idname) },
            { 'border-gray-700': !get(errors, idname) }
          )}
        />
        <button
          className="absolute right-2 bg-gray-800 flex items-center text-primary-200 top-2 bottom-2 text-xs px-2 rounded"
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
        >
          <div>{showPassword ? 'Hide' : 'Show'}</div>
        </button>
      </div>
      <p className="text-error-500 mt-1"> {get(errors, idname)?.message}</p>
    </div>
  );
}
